import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import {
  Template,
  TemplateRequest,
  CreateTemplateVersionResponse,
  GetTemplatesQueryParams,
  UploadUrlResponse,
  DownloadUrlResponse,
  AgentStatusResponse,
  TemplateProcessStatus
} from '../models/templates';
import { WorkspaceTemplateService } from '../services/templates.service';

const pdfLink = 'https://www.aeee.in/wp-content/uploads/2020/08/Sample-pdf.pdf';
const mockTemplates: Template[] = [
  {
    id: 'temp-101',
    name: 'Standard Project Setup',
    version: '2.0.0',
    is_latest: true,
    description: 'Lorem ipsum',
    s3_key: 'test-key',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: 'user-1',
    default_personas: ['pers-1'],
    process_status: TemplateProcessStatus.NOT_UPLOADED
  },
  {
    id: 'temp-101',
    name: 'Standard Project Setup',
    version: '1.0.0',
    is_latest: false,
    description: 'Lorem ipsum',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: 'user-1',
    default_personas: ['pers-1'],
    process_status: TemplateProcessStatus.NOT_UPLOADED
  },
  {
    id: 'temp-205',
    name: 'R&D Experiment Template',
    version: '1.5.0',
    is_latest: true,
    description: 'Lorem ipsum',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by: 'user-1',
    default_personas: ['pers-1'],
    process_status: TemplateProcessStatus.NOT_UPLOADED
  }
];

@Injectable()
export class MockWorkspaceTemplateService extends WorkspaceTemplateService implements WorkspaceTemplateService {
  public override getTemplates(params?: GetTemplatesQueryParams): Observable<Template[]> {
    let result = [...mockTemplates];

    if (params?.show_latest) {
      result = result.filter(t => t.is_latest);
    }

    return of(result).pipe(delay(2000));
  }

  public override createTemplate(data: TemplateRequest): Observable<CreateTemplateVersionResponse> {
    const newTemplateId = `temp-${Date.now()}`;
    console.log(`MOCK: Creating new template: ${data.name}`);

    mockTemplates.push({
      id: newTemplateId,
      name: data.name,
      version: '1.0.0',
      is_latest: true,
      description: data.description,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: 'user-1',
      default_personas: data.default_personas ?? [],
      process_status: TemplateProcessStatus.NOT_UPLOADED
    });

    return of({
      id: newTemplateId,
      version: '1.0.0'
    }).pipe(delay(2000));
  }

  public override createTemplateVersion(
    templateId: string,
    data: TemplateRequest
  ): Observable<CreateTemplateVersionResponse> {
    const newVersion = '2.1.0';
    console.log(`MOCK: Creating new version ${newVersion} for template ID: ${templateId}`);

    const latestIndex = mockTemplates.findIndex(t => t.id === templateId && t.is_latest);
    if (latestIndex > -1) {
      mockTemplates.push({
        id: templateId,
        name: data.name,
        version: newVersion,
        is_latest: true,
        description: data.description,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: 'user-1',
        default_personas: data.default_personas ?? [],
        process_status: TemplateProcessStatus.NOT_UPLOADED
      });
    }

    return of({
      id: templateId,
      version: newVersion
    }).pipe(delay(2000));
  }

  public override getTemplateVersions(templateId: string): Observable<Template[]> {
    console.log(`MOCK: Getting versions for template ID: ${templateId}`);
    return of(mockTemplates.filter(t => t.id === templateId)).pipe(delay(500));
  }

  public override generateUploadUrl(
    templateId: string,
    version: string,
    extension: string
  ): Observable<UploadUrlResponse> {
    console.log(
      `MOCK: Generating upload URL for template ID: ${templateId}, version: ${version}, extension: ${extension}`
    );
    return of({
      file_name: `test-file.${extension}`,
      s3_key: `templates/${templateId}/${version}/file.${extension}`,
      url: 'http://localhost:4200/upload-url'
    }).pipe(delay(500));
  }
  public override generateDownloadUrl(templateId: string, version: string): Observable<DownloadUrlResponse> {
    console.log(`MOCK: Generating download URL for template ID: ${templateId}, version: ${version}`);
    return of({
      url: pdfLink
    }).pipe(delay(500));
  }

  public override triggerTemplateAgent(templateId: string, version: string): Observable<{ message: string }> {
    console.log(`MOCK: Triggering agent for template ID: ${templateId}, version: ${version}`);
    return of({ message: 'OK' }).pipe(delay(500));
  }

  public override getAgentRunStatus(templateId: string, version: string): Observable<AgentStatusResponse> {
    console.log(`MOCK: Getting agent status for template ID: ${templateId}, version: ${version}`);
    return of({ status: 'COMPLETED' }).pipe(delay(500));
  }
}
